import { useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'

// Thin hook over AuthContext.api for the Ticker Data module. Memoized so
// callers can list it as a useEffect dep (same reason as useTeamsDataApi).
//
// Seasons come from the Team Scheduler's own endpoint rather than a duplicate:
// the ticker reads the games the scheduler already tracks.
export function useTickerApi() {
  const { api } = useAuth()

  return useMemo(() => ({
    listSeasons() {
      return api('/scheduler/seasons')
    },
    listResults({ season, league, from, to, limit = 200 } = {}) {
      const qs = new URLSearchParams()
      if (season) qs.set('season', season)
      if (league) qs.set('league', league)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      if (limit) qs.set('limit', String(limit))
      return api(`/ticker/results?${qs.toString()}`)
    },
    // Box scores are the module's input; fetching them is the Team Scheduler's
    // endpoint, surfaced here so this page is usable on its own.
    boxStatus(seasonId) {
      return api(`/scheduler/seasons/${seasonId}/boxscores`)
    },
    fetchBoxScores(seasonId, { limit = 40, refresh = false, from, to } = {}) {
      const qs = new URLSearchParams({ limit: String(limit) })
      if (refresh) qs.set('refresh', '1')
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      return api(`/scheduler/seasons/${seasonId}/boxscores?${qs}`, { method: 'POST' })
    },
    freshness(seasonId) {
      return api(`/scheduler/seasons/${seasonId}/freshness`)
    },
    syncSeason(seasonId) {
      return api(`/scheduler/seasons/${seasonId}/sync`, { method: 'POST' })
    },
    statsStatus(seasonId) {
      return api(`/scheduler/seasons/${seasonId}/stats`)
    },
    fetchStats(seasonId) {
      return api(`/scheduler/seasons/${seasonId}/stats`, { method: 'POST' })
    },
    listStandings(seasonId, { group, top = 5 } = {}) {
      const qs = new URLSearchParams({ season: String(seasonId), top: String(top) })
      if (group) qs.set('group', group)
      return api(`/ticker/standings?${qs}`)
    },
    listLeaders(seasonId, { group, metric = 'points', top = 6 } = {}) {
      const qs = new URLSearchParams({ season: String(seasonId), metric, top: String(top) })
      if (group) qs.set('group', group)
      return api(`/ticker/leaders?${qs}`)
    },
  }), [api])
}
